import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import axios from "axios";
import {
  BookOpen, Users, Award, Briefcase, FileCheck, ArrowLeft,
  MapPin, Clock, Send, ChevronDown, ChevronUp, Check, Upload, FileText,
  GraduationCap, Star
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useSettings } from "@/contexts/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceIcons = [BookOpen, Users, Briefcase, Award, FileCheck, GraduationCap, Star, Check];
const featureIcons = [Award, GraduationCap, Users, Star, BookOpen, Briefcase];

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay }} className={className}>
      {children}
    </motion.div>
  );
}

const jobTypeLabels = {
  "full-time": "دوام كامل",
  "part-time": "دوام جزئي",
  "remote": "عن بعد",
  "contract": "عقد مؤقت",
};

function JobCard({ job, onApply }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-[#161618] border border-white/5 rounded-xl overflow-hidden hover:border-primary/20 transition-all" data-testid={`job-card-${job.id}`}>
      <div className="p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-bold font-['Zain'] text-white mb-1">{job.title}</h3>
            <p className="text-zinc-400 text-sm">{job.department}</p>
          </div>
          <Badge className="bg-primary/10 text-primary border-0 flex-shrink-0">
            {jobTypeLabels[job.job_type] || job.job_type}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-500 mb-4">
          <span className="flex items-center gap-1.5"><MapPin size={14} className="text-primary" />{job.location}</span>
          <span className="flex items-center gap-1.5"><Clock size={14} className="text-primary" />{jobTypeLabels[job.job_type] || job.job_type}</span>
        </div>
        <p className="text-zinc-400 text-sm leading-relaxed mb-4">{job.description}</p>
        {job.requirements?.length > 0 && (
          <div>
            <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 text-primary text-sm font-medium mb-2 hover:text-[#E54D2E] transition-colors" data-testid={`job-toggle-${job.id}`}>
              المتطلبات {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {expanded && (
              <motion.ul initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-1.5 mb-4">
                {job.requirements.map((r, i) => (
                  <li key={i} className="flex items-center gap-2 text-zinc-400 text-sm"><Check size={14} className="text-primary flex-shrink-0" />{r}</li>
                ))}
              </motion.ul>
            )}
          </div>
        )}
        <Button onClick={() => onApply(job)} className="bg-primary hover:bg-[#E54D2E] text-white w-full mt-2" data-testid={`job-apply-${job.id}`}>
          قدّم الآن <Send size={14} className="ms-2" />
        </Button>
      </div>
    </div>
  );
}

function ApplicationDialog({ job, open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", cv_url: "", cover_letter: "" });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cvFileName, setCvFileName] = useState("");

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (![".pdf", ".doc", ".docx"].includes(ext)) { toast.error("يُقبل فقط ملفات PDF و Word"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("حجم الملف يجب أن لا يتجاوز 10 ميغابايت"); return; }
    setUploading(true);
    try {
      const formData = new FormData(); formData.append("file", file);
      const { data } = await axios.post(`${API}/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(prev => ({ ...prev, cv_url: data.url })); setCvFileName(file.name);
      toast.success("تم رفع الملف بنجاح");
    } catch { toast.error("حدث خطأ في رفع الملف"); }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { toast.error("يرجى ملء الحقول المطلوبة"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/jobs/apply`, { ...form, job_id: job.id, job_title: job.title });
      toast.success("تم إرسال طلبك بنجاح! سنتواصل معك قريبًا");
      setForm({ name: "", email: "", phone: "", cv_url: "", cover_letter: "" }); onClose();
    } catch { toast.error("حدث خطأ. يرجى المحاولة لاحقًا"); }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#161618] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-['Zain'] text-right">التقديم على: {job?.title}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="application-form">
          <div>
            <label className="text-zinc-400 text-sm mb-1.5 block">الاسم الكامل *</label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-background border-white/10 text-white text-right" placeholder="اسمك الكامل" data-testid="app-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block">البريد الإلكتروني *</label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-background border-white/10 text-white" placeholder="email@example.com" dir="ltr" data-testid="app-email" />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1.5 block">الهاتف *</label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-background border-white/10 text-white" placeholder="+966 5X XXX XXXX" dir="ltr" data-testid="app-phone" />
            </div>
          </div>
          <div>
            <label className="text-zinc-400 text-sm mb-1.5 block">السيرة الذاتية</label>
            {cvFileName ? (
              <div className="flex items-center gap-3 p-3 bg-background rounded-lg border border-primary/20">
                <FileText size={20} className="text-primary" />
                <span className="text-zinc-300 text-sm flex-1 truncate">{cvFileName}</span>
                <Button type="button" variant="ghost" size="sm" className="text-zinc-400 hover:text-white h-7 px-2" onClick={() => { setCvFileName(""); setForm(prev => ({ ...prev, cv_url: "" })); }}>تغيير</Button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 p-4 bg-background rounded-lg border border-dashed border-white/10 cursor-pointer hover:border-primary/30 transition-colors" data-testid="app-cv-upload">
                {uploading ? <span className="text-zinc-400 text-sm">جاري الرفع...</span> : <><Upload size={18} className="text-zinc-500" /><span className="text-zinc-400 text-sm">اختر ملف أو اسحبه هنا</span></>}
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleFileUpload} disabled={uploading} />
              </label>
            )}
          </div>
          <div>
            <label className="text-zinc-400 text-sm mb-1.5 block">رسالة التقديم</label>
            <Textarea value={form.cover_letter} onChange={e => setForm({ ...form, cover_letter: e.target.value })} className="bg-background border-white/10 text-white text-right min-h-[100px]" placeholder="لماذا تريد الانضمام لفريقنا؟" data-testid="app-cover" />
          </div>
          <Button type="submit" disabled={loading} className="bg-primary hover:bg-[#E54D2E] text-white w-full" data-testid="app-submit">
            {loading ? "جاري الإرسال..." : "إرسال الطلب"} <Send size={14} className="ms-2" />
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function TrainingRecruitmentPage() {
  const settings = useSettings();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/jobs?active_only=true`).then(r => setJobs(r.data)).catch(() => {});
  }, []);

  const trServices = settings.tr_training_services?.length ? settings.tr_training_services : [];
  const whyFeatures = settings.tr_why_features?.length ? settings.tr_why_features : [];

  return (
    <div data-testid="training-recruitment-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={settings.training_image || "https://images.pexels.com/photos/8761519/pexels-photo-8761519.jpeg?auto=compress&cs=tinysrgb&w=1920"}
            alt="" className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#111112]/50 to-[#111112]" />
        </div>
        <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-primary/10 rounded-full blur-[120px]" />
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-block px-4 py-1.5 rounded-full border border-primary/30 text-primary text-sm font-medium mb-4">
              {settings.tr_hero_badge || "التدريب والتوظيف"}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Zain'] text-white leading-tight mb-4">
              {(settings.tr_hero_title || "نصنع الكفاءات").split(" ").map((w, i, arr) =>
                i === arr.length - 1 ? <span key={i} className="text-primary">{w}</span> : <span key={i}>{w} </span>
              )}
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
              {settings.tr_hero_subtitle || "نطوّر المهارات ونوفر الفرص المهنية لبناء مستقبل أفضل للأفراد والمنشآت."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Training Services */}
      {trServices.length > 0 && (
        <section className="py-20 md:py-28">
          <div className="container mx-auto px-4 md:px-6">
            <Reveal className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold font-['Zain'] text-white">
                {settings.tr_training_title || "برامجنا التدريبية"}
              </h2>
              <p className="text-zinc-500 text-sm mt-3 max-w-md mx-auto">
                {settings.tr_training_subtitle || "خدمات تدريبية متكاملة لرفع كفاءة الأفراد والفرق"}
              </p>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {trServices.map((s, i) => {
                const Icon = serviceIcons[i % serviceIcons.length];
                return (
                  <Reveal key={i} delay={i * 0.07}>
                    <div className="bg-[#161618] border border-white/5 rounded-xl p-6 hover:border-primary/20 transition-all group" data-testid={`training-service-${i}`}>
                      <div className="w-12 h-12 rounded-xl bg-primary/[0.08] border border-primary/[0.12] flex items-center justify-center mb-4 group-hover:bg-primary/[0.15] transition-colors">
                        <Icon size={22} className="text-primary" />
                      </div>
                      <h3 className="text-lg font-bold font-['Zain'] text-white mb-2">{s.title}</h3>
                      <p className="text-zinc-400 text-sm leading-relaxed">{s.desc}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Why Join */}
      {whyFeatures.length > 0 && (
        <section className="py-16 border-y border-white/5 bg-[#0d0d0e]">
          <div className="container mx-auto px-4 md:px-6">
            <Reveal className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-['Zain'] text-white">
                {settings.tr_why_title || "لماذا تنضم إلينا؟"}
              </h2>
            </Reveal>
            <div className={`grid grid-cols-1 md:grid-cols-${Math.min(whyFeatures.length, 3)} gap-5`}>
              {whyFeatures.map((item, i) => {
                const Icon = featureIcons[i % featureIcons.length];
                return (
                  <Reveal key={i} delay={i * 0.08}>
                    <div className="bg-[#161618] border border-white/5 rounded-xl p-6 text-center hover:border-primary/20 transition-all group" data-testid={`why-join-${i}`}>
                      <div className="w-12 h-12 rounded-xl bg-primary/[0.08] border border-primary/[0.12] flex items-center justify-center mb-4 mx-auto group-hover:bg-primary/[0.15] transition-colors">
                        <Icon size={22} className="text-primary" />
                      </div>
                      <h3 className="text-lg font-bold font-['Zain'] text-white mb-2">{item.title}</h3>
                      <p className="text-zinc-400 text-sm">{item.desc}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Jobs */}
      <section className="py-20 md:py-28 border-t border-white/5">
        <div className="container mx-auto px-4 md:px-6">
          <Reveal className="text-center mb-12">
            <span className="text-primary text-sm font-medium">{settings.tr_jobs_title || "الوظائف المتاحة"}</span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Zain'] text-white mt-2">
              {jobs.length} فرصة متاحة حاليًا
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {jobs.map((job) => (
              <Reveal key={job.id}>
                <JobCard job={job} onApply={(j) => { setSelectedJob(j); setDialogOpen(true); }} />
              </Reveal>
            ))}
          </div>

          {jobs.length === 0 && (
            <div className="text-center py-16 text-zinc-500">
              <Briefcase size={48} className="mx-auto mb-4 text-zinc-700" />
              <p>لا توجد وظائف متاحة حاليًا. تابعنا للحصول على آخر الفرص.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <Reveal>
            <h2 className="text-2xl md:text-4xl font-bold font-['Zain'] text-white mb-6">
              {settings.tr_cta_title || "سجّل الآن في دوراتنا"}
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/courses">
                <Button size="lg" className="bg-primary hover:bg-[#E54D2E] text-white px-8 red-glow" data-testid="training-courses-btn">
                  تصفح الدورات <ArrowLeft size={16} className="ms-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white/10 text-white hover:bg-white/5 px-8" data-testid="training-contact-btn">
                  تواصل معنا
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <ApplicationDialog job={selectedJob} open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </div>
  );
}
